import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import {
  DEFAULT_LOGISTIC_PROVIDER_VALUES,
  LogisticProviderForm,
  NewLogisticProviderForm,
  logisticProviderFormSchema,
} from '@lp/features/admin/forms/LogisticProviderForm';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { useCreateTXCenterProviderMutation } from '@/queries/txcenter-providers';
import { useCreatePartnerProviderMutation } from '@/queries/partner-providers';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';
import { convertToIanaTimezone } from '@/utils/helpers';

type LogisticProvidersCreateProps = {
  type: string;
};

export const LogisticProvidersCreate = ({ type }: LogisticProvidersCreateProps) => {
  const navigate = useNavigate();
  const { mutateAsync: mutateAsyncTXCenterPartner } = useCreateTXCenterProviderMutation();
  const { mutateAsync: mutateAsyncPartner } = useCreatePartnerProviderMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);

  const isPartnerProvider = type === 'Partner';
  const title = `Create New ${isPartnerProvider ? 'Supplemental Lift' : 'TX Center'} Provider`;
  const backToListLink = `/admin/logistics_providers/${isPartnerProvider ? 'supplemental_lift' : 'tx_center'}`;

  const handleBackToList = () => {
    navigate(backToListLink);
  };

  const handleSubmit = async (data: NewLogisticProviderForm) => {
    try {
      let uploadedFile;

      if (data.image) {
        uploadedFile = await uploadFiles([data.image]);
      }

      const body = {
        id: uuidV4(),
        name: data.name,
        email: data.email,
        primaryPhone: data.primaryPhone,
        secondaryPhone: data.secondaryPhone,
        aircraftType: data.aircraftType,
        coverage: Object.entries(data.coverage).map(([country, subdivisions]) => ({ country, subdivisions })),
        country: data.country,
        state: data.state,
        addressLine: data.addressLine,
        city: data.city,
        postalCode: data.postalCode,
        timezone: data.timezoneIANA || convertToIanaTimezone(data.timezone),
        logoPath: uploadedFile ? uploadedFile[0].fileName : data.logoPath,
      };

      if (isPartnerProvider) {
        await mutateAsyncPartner(body);
      } else {
        await mutateAsyncTXCenterPartner(body);
      }

      toast.success(`Logistics Provider created successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Form<NewLogisticProviderForm>
      defaultValues={DEFAULT_LOGISTIC_PROVIDER_VALUES}
      schema={logisticProviderFormSchema}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-9">
        <AdminPageHeader title={title} backToListLink={backToListLink}>
          <AdminFormAction type="create" handleClick={handleBackToList} hasCreatePermissions={hasCreatePermissions} />
        </AdminPageHeader>
        <LogisticProviderForm isReadOnly={!hasCreatePermissions} isPartnerProvider={isPartnerProvider} />
      </div>
    </Form>
  );
};
